import { Link } from "react-router";
import { Card, CardHeader, Tag } from "./ui";
import { formatRelative } from "../lib/format";
import { useOrgRisk, useRiskRules } from "../lib/queries";
import { SEVERITY_LABEL, SEVERITY_TONE } from "../lib/risk";

/**
 * What the safety rules have seen of this organization, on its own page.
 * Nothing when nothing has ever fired: a card that always says "no
 * concerns" trains the eye to skip the place concerns appear.
 */
export function OrgRiskCard({ orgId }: { orgId: string }) {
  const risk = useOrgRisk(orgId);
  const rules = useRiskRules();
  if (!risk.data || risk.data.signals.length === 0) return null;
  const { openCase, signals, cases } = risk.data;
  return (
    <Card>
      <CardHeader
        title="Safety"
        description={
          openCase
            ? `An open case, score ${openCase.score}${openCase.status === "reviewing" ? ", being reviewed" : ""}.`
            : `${cases.total} earlier case${cases.total === 1 ? "" : "s"}${cases.suspended ? `, ${cases.suspended} ending in suspension` : ""}.`
        }
        action={
          openCase ? (
            <Link to={`/safety/${openCase.id}`} className="text-[13px] text-accent hover:underline">
              Open the case
            </Link>
          ) : undefined
        }
      />
      <ul className="divide-y divide-line">
        {signals.slice(0, 8).map((signal) => (
          <li key={signal.id} className="flex flex-wrap items-center gap-2 px-5 py-2.5 text-sm">
            <Tag tone={SEVERITY_TONE[signal.severity]}>{SEVERITY_LABEL[signal.severity]}</Tag>
            <span className="text-fg">{rules.data?.get(signal.kind)?.title ?? signal.kind}</span>
            <span className="text-fg-subtle">— {signal.summary}</span>
            <span className="ml-auto text-xs text-fg-subtle">{formatRelative(signal.createdAt)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
